"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { ProfileCard, ProfileCardData } from "./ProfileCard";
import { GamProfile } from "@/lib/database/gam-profiles";
import { fetchGamProfiles } from "@/lib/database/client/database";
import { CTA } from "@/app/(public)/shared/components/CTA";
import nowadaysBalloons from "@/app/(public)/shared/assets/nowadays-balloons.png";
import { useEffect, useState } from "react";
import { createClient } from "@/utils/supabase/client";
import { motion } from "framer-motion";

export default function GamProfiles() {
  const supabase = createClient();
  const [profiles, setProfiles] = useState<GamProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch profiles on component mount
  useEffect(() => {
    const loadProfiles = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchGamProfiles(supabase);
        setProfiles(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load profiles");
        console.error("Error fetching GAM profiles:", err);
      } finally {
        setLoading(false);
      }
    };

    loadProfiles();
  }, []);

  // Convert database profiles to ProfileCardData format and sort alphabetically by name
  const profileCards: ProfileCardData[] = profiles
    .map((profile) => ({
      id: profile.slug,
      name: profile.name,
      title: "Global Account Manager",
      expertise: profile.expertise,
      basedIn: profile.based_in,
      profileImage: profile.profile_image || nowadaysBalloons,
      shortBio: profile.about.split("\n")[0] || profile.about.substring(0, 200),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-landing-bg-primary" data-section="gam-profiles">
        {/* Hero Section Skeleton */}
        <div className="w-full flex justify-center items-center py-12 sm:py-24">
          <div className="container-page flex flex-col gap-8 sm:gap-16 pt-16 sm:pt-32">
            <div className="w-full flex flex-col justify-start items-start gap-6">
              <div className="h-12 sm:h-20 w-64 sm:w-96 bg-gray-200 rounded animate-pulse" />
              <div className="h-6 sm:h-8 w-full max-w-3xl bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </div>

        {/* Profiles Grid Skeleton */}
        <div className="w-full flex justify-center items-center pb-12 sm:pb-24">
          <div className="container-page flex flex-col gap-8 sm:gap-12">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-96 bg-gray-200 rounded-2xl animate-pulse" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen container-page bg-landing-bg-primary px-4">
        <div className="flex items-center justify-center py-12 sm:py-24">
          <div className="text-center">
            <div className="text-body-lg text-red-600">Error: {error}</div>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-landing-bg-primary overflow-x-hidden" data-section="gam-profiles">
      {/* Hero Section */}
      <div className="w-full flex justify-center items-center py-12 sm:py-24">
        <div className="container-page flex flex-col gap-8 sm:gap-16 pt-16 sm:pt-32">
          <motion.div
            className="w-full flex flex-col justify-start items-start gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              className="self-stretch justify-start text-h1 text-landing-primary"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Meet Our Global Account Managers
            </motion.div>
            <motion.div
              className="self-stretch justify-start text-body-lg text-landing-secondary max-w-3xl"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Connect with our expert travel specialists who bring local knowledge and global connections to create
              exceptional events tailored to your needs.
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Profiles Grid */}
      <div className="w-full flex justify-center items-center pb-12 sm:pb-24">
        <div className="container-page flex flex-col gap-8 sm:gap-12">
          {profileCards.length === 0 ? (
            <motion.div
              className="text-center py-12 sm:py-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-h3 mb-4 text-landing-primary">No profiles available</div>
              <div className="text-body-lg landing-text mb-6 text-landing-secondary">
                Please check back later for our Global Account Manager profiles.
              </div>
            </motion.div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {profileCards.map((profile) => (
                <ProfileCard key={profile.id} profile={profile} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="w-full flex justify-center items-center pb-12 sm:pb-24">
        <div className="container-page">
          <CTA />
        </div>
      </div>
    </div>
  );
}
